import { ChevronDown, ChevronUp, FileText, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'

interface NotesSectionProps {
  notes: string | null | undefined
  onUpdate: (data: { notes: string }) => Promise<unknown>
}

export function NotesSection({ notes, onUpdate }: NotesSectionProps) {
  const [value, setValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    state: saveState,
    hasUnsavedChanges: hasChanges,
    onChange: scheduleSave,
    cancel: cancelSave,
  } = useAutoSave({
    value,
    onSave: (next: string) => onUpdate({ notes: next }),
    debounceMs: AUTO_SAVE_DEBOUNCE_MS.medium,
    logLabel: 'Notes',
  })

  useEffect(() => {
    setValue(notes || '')
    // The server is the authority again: whatever was typed before is stale.
    cancelSave()
  }, [notes, cancelSave])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
    scheduleSave(e.target.value)
  }

  const saving = saveState === 'saving'

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="border-border hover:bg-muted/50 flex w-full items-center justify-between border-b p-4 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-muted-foreground" />
          <h2 className="text-foreground text-lg font-semibold">Notes</h2>
          {hasChanges && (
            <span className="text-primary flex items-center gap-1 text-xs">
              <Save size={12} />
              {saving ? 'Saving...' : 'Unsaved changes'}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={20} className="text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          <textarea
            value={value}
            onChange={handleChange}
            placeholder="Add notes about this client..."
            className="bg-muted border-border text-foreground placeholder:text-muted-foreground min-h-[200px] w-full resize-y rounded-lg border p-3"
          />
          <p className="text-muted-foreground mt-2 text-xs">
            Notes are saved automatically after you stop typing.
          </p>
        </div>
      )}
    </div>
  )
}
